import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import {
  ensureTurnstilePortalStyle,
  removeTurnstileWidget,
  renderTurnstileWidget,
  resetTurnstileWidget,
  TURNSTILE_PORTAL_ATTR,
  type TurnstileWidgetId,
} from '../api/turnstile';

/**
 * `<embrace-chat-captcha>` — Turnstile-widget voor de conversational
 * intake POC. Verschijnt als een bubble in de chat-stream wanneer
 * `state.assistant.step === 'awaiting-captcha'`. Op success dispatcht
 * de component een `@captcha-verified` CustomEvent met de token.
 *
 * Portal-pattern (geleend uit intake-form.ts): Turnstile's renderer
 * faalt soms in shadow-DOM en heeft een vaste min-breedte (300px).
 * We renderen in een `<div>` in `document.body`, gepositioneerd
 * absoluut over onze placeholder. Scroll/resize-listeners houden
 * hem in sync.
 */
@customElement('embrace-chat-captcha')
export class EmbraceChatCaptcha extends LitElement {
  @property({ type: String }) sitekey = '';

  @query('.host')
  private host?: HTMLDivElement;

  private portal: HTMLDivElement | null = null;
  private widgetId: TurnstileWidgetId | null = null;
  private repositionRaf: number | null = null;

  static override styles = css`
    :host {
      display: block;
      padding: 0 12px 12px;
    }
    .bubble {
      background: #fffbeb;
      border: 1px solid #fde68a;
      border-radius: 12px;
      padding: 10px;
    }
    .host {
      width: 100%;
      min-height: 65px;
    }
    .error {
      color: #9f1239;
      font: 400 12px/1.4 var(--ec-font-family, system-ui);
      margin-top: 6px;
    }
  `;

  @property({ state: true })
  private error = '';

  override render() {
    return html`
      <div class="bubble">
        <div class="host"></div>
        ${this.error
          ? html`<div class="error">${this.error}</div>`
          : null}
      </div>
    `;
  }

  override firstUpdated(): void {
    if (this.sitekey) this.mountTurnstile();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unmountTurnstile();
  }

  private async mountTurnstile(): Promise<void> {
    if (!this.host || this.portal) return;
    ensureTurnstilePortalStyle();
    const portal = document.createElement('div');
    portal.setAttribute(TURNSTILE_PORTAL_ATTR, '');
    portal.style.cssText =
      'position: fixed; z-index: 1000001; pointer-events: auto;';
    document.body.appendChild(portal);
    this.portal = portal;
    this.applyPortalPosition();
    window.addEventListener('scroll', this.updatePortalPosition, true);
    window.addEventListener('resize', this.updatePortalPosition);
    try {
      const widgetId = await renderTurnstileWidget(portal, {
        sitekey: this.sitekey,
        theme: 'light',
        language: 'nl',
        size: 'flexible',
        callback: (token: string) => {
          this.error = '';
          this.dispatchEvent(
            new CustomEvent('captcha-verified', {
              detail: { token },
              bubbles: true,
              composed: true,
            }),
          );
        },
        'error-callback': (code: string) => {
          this.error = `Captcha fout (${code}). Probeer de pagina te verversen.`;
        },
        'expired-callback': () => {
          if (this.widgetId) resetTurnstileWidget(this.widgetId);
        },
        'timeout-callback': () => {
          if (this.widgetId) resetTurnstileWidget(this.widgetId);
        },
      });
      if (!this.portal) {
        // Disconnect tijdens await — niet vasthouden.
        removeTurnstileWidget(widgetId);
        return;
      }
      this.widgetId = widgetId;
      this.applyPortalPosition();
    } catch (err) {
      this.error =
        'De beveiligingscheck kon niet geladen worden. Probeer de pagina te verversen.';
      console.error('[chat-captcha] mount failed:', err);
    }
  }

  private unmountTurnstile(): void {
    if (this.widgetId) {
      removeTurnstileWidget(this.widgetId);
      this.widgetId = null;
    }
    if (this.portal) {
      this.portal.remove();
      this.portal = null;
    }
    window.removeEventListener('scroll', this.updatePortalPosition, true);
    window.removeEventListener('resize', this.updatePortalPosition);
    if (this.repositionRaf !== null) {
      cancelAnimationFrame(this.repositionRaf);
      this.repositionRaf = null;
    }
  }

  private applyPortalPosition(): void {
    if (!this.portal || !this.host) return;
    const rect = this.host.getBoundingClientRect();
    this.portal.style.left = `${rect.left}px`;
    this.portal.style.top = `${rect.top}px`;
    this.portal.style.width = `${rect.width}px`;
  }

  private readonly updatePortalPosition = (): void => {
    if (this.repositionRaf !== null) return;
    this.repositionRaf = requestAnimationFrame(() => {
      this.repositionRaf = null;
      this.applyPortalPosition();
    });
  };

  override updated(): void {
    if (!this.portal) return;
    this.updatePortalPosition();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-chat-captcha': EmbraceChatCaptcha;
  }
}
